import { Injectable, inject, computed, signal } from "@angular/core";
import { IFirmRolesService, MANAGER_ROLE_ID, PARTNER_ROLE_ID, UnassignedUserId, UsersService } from "../shared";
import { ITranslateObject, JobsBillingCard } from "../views-interfaces";
import { MultiSelectModel, Option } from "@app/filter-menu/filter-types";
import { CardsService } from "@services/index";

export class AssigneeFilterValue {
    userId: string;
    roleId: string | null;
}

@Injectable()
export class AssigneesFilterGeneratorService {
    private readonly usersService = inject(UsersService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly cardsService = inject(CardsService);

    selection = signal<object>({});

    readonly models = computed<MultiSelectModel[]>(
        () => {
            const selection = this.selection();

            const models: MultiSelectModel[] = [];
            const usersByRoles = this.iFirmRolesService.rolesWithUsers();
            this.iFirmRolesService.iFirmRoles
                .slice()
                .sort((a, b) => {
                    const roleOrder: Record<string, number> = {
                        [PARTNER_ROLE_ID]: 0,
                        [MANAGER_ROLE_ID]: 1,
                    };
                    const aOrder = roleOrder[a.Id];
                    const bOrder = roleOrder[b.Id];
                    if (aOrder !== undefined && bOrder !== undefined) return aOrder - bOrder;
                    if (parseInt(a.Id, 10) < parseInt(b.Id, 10)) return -1;
                    if (parseInt(a.Id, 10) > parseInt(b.Id, 10)) return 1;
                    return 0;
                })
                .filter((role) => [PARTNER_ROLE_ID, MANAGER_ROLE_ID].includes(role.Id))
                .forEach((role) => {
                    const options: Option[] = [
                        {
                            text: "JobsAndBilling.Contact.None",
                            value: JSON.stringify({ userId: null, roleId: role.Id }),
                            isVisible: (_: unknown) => true,
                        },
                    ].concat(
                        Array.from(usersByRoles.get(role.Id)?.Users?.values() ?? [])
                            .sort((a, b) => {
                                const byActive = Number(b.Active) - Number(a.Active);
                                if (byActive !== 0) return byActive;

                                return `${a.FirstName}  ${a.LastName}`.localeCompare(`${b.FirstName}  ${b.LastName}`);
                            })
                            .map((entry) => {
                                return {
                                    text: entry.FullName,
                                    value: JSON.stringify({ userId: entry.Id, roleId: role.Id }),
                                    isVisible: (_: unknown) =>
                                        entry.Active || (this.cardsService.getCardsCountForUser(entry).totalByRole?.[role.Id] ?? 0) > 0,
                                };
                            }),
                    );
                    const applicableuserRoleOptions = options.filter((option) =>
                        option.isVisible ? (typeof option.isVisible === "boolean" ? option.isVisible : option.isVisible(undefined)) : true,
                    );
                    const userRoleselection = selection ? (selection[`role-${role.Id}`] ?? []) : [];
                    models.push({
                        id: `role-${role.Id}`,
                        label: role.Description,
                        allOptions: options,
                        applicableOptions: applicableuserRoleOptions,
                        selection: userRoleselection.filter(
                            (selected) => selected && applicableuserRoleOptions.some((option) => option && option?.value === selected?.value),
                        ),
                    } as MultiSelectModel);
                });
            return models;
        },
        { equal: (a, b) => this.modelsEqual(a, b) },
    );

    private modelsEqual(a: MultiSelectModel[], b: MultiSelectModel[]): boolean {
        if (a === b) return true;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i].id !== b[i].id) return false;
            if (!this.optionsEqual(a[i].applicableOptions, b[i].applicableOptions)) return false;
            if (!this.optionsEqual(a[i].selection, b[i].selection)) return false;
        }
        return true;
    }

    private optionsEqual(a: Option[], b: Option[]): boolean {
        if (a === b) return true;
        const aLen = a?.length ?? 0;
        const bLen = b?.length ?? 0;
        if (aLen !== bLen) return false;
        for (let i = 0; i < aLen; i++) {
            if (a[i]?.value !== b[i]?.value || a[i]?.text !== b[i]?.text) return false;
        }
        return true;
    }

    resetCache() {
        this.selection.set({});
    }

    loadModels(selection: object) {
        this.selection.set(selection);
    }

    updateSelection(modelId: number, selection: Option[]): MultiSelectModel | undefined {
        const selectionType = this.models()[modelId]?.id;
        if (selectionType) {
            this.selection.update((prev) => {
                const next = { ...prev };
                next[selectionType] = selection;
                return next;
            });
        }
        return this.models()[modelId];
    }

    updateModel(modelId: string, selection: Option[]): MultiSelectModel | undefined {
        const modelIndex = this.models().findIndex((model) => model.id === modelId);
        if (modelIndex !== -1) {
            return this.updateSelection(modelIndex, selection);
        }
    }

    getPrintDescription(
        label: string | ITranslateObject,
        selectedValues: string[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        }

        const values: (string | ITranslateObject)[] = selectedValues.map((value) => {
            const user = this.usersService.getUserById(JSON.parse(value)?.userId);
            return user
                ? ({
                      "en-CA": `${user.FirstName} ${user.LastName}`,
                      "fr-CA": `${user.FirstName} ${user.LastName}`,
                  } as ITranslateObject)
                : "FiltersSidebar.Options.Users.Unassigned";
        });

        return { label, values };
    }

    generateFilter(selectedValues: string[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            const selection = selectedValues.map((value) => JSON.parse(value) as AssigneeFilterValue);

            return (card: JobsBillingCard) => {
                return selection.some((value) => {
                    if (!card.contactInformation) {
                        return value.userId === null;
                    }

                    if (value.roleId === null) {
                        const partnerMatch = this.userIdMatch(card.contactInformation.partner, value.userId);
                        const managerMatch = this.userIdMatch(card.contactInformation.manager, value.userId);
                        return partnerMatch || managerMatch;
                    }

                    const contactUserId = value.roleId === PARTNER_ROLE_ID ? card.contactInformation.partner : card.contactInformation.manager;
                    return this.userIdMatch(contactUserId, value.userId);
                });
            };
        }
    }

    userIdMatch(mappingUserId: string, expected: string) {
        const mappingUser = this.usersService.getUserById(mappingUserId);
        return (
            mappingUser?.Id === expected ||
            (expected === null &&
                (mappingUserId === null || mappingUserId === undefined || mappingUserId === "" || mappingUserId === UnassignedUserId))
        );
    }
}
